import { Injectable, OnDestroy } from '@angular/core';

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  hits: number;
  createdAt: number;
}

/**
 * TtlCacheService — Bounded, TTL-based cache for Angular services.
 *
 * Prevents singleton services from accumulating unbounded data maps.
 *
 * Features:
 * - TTL per entry (default: 10 minutes)
 * - Maximum entry cap with LRU eviction (default: 200 entries)
 * - Automatic expired-entry sweep every 5 minutes
 * - flush() for full reset during soft reload
 */
@Injectable({ providedIn: 'root' })
export class TtlCacheService implements OnDestroy {
  private readonly store = new Map<string, CacheEntry<unknown>>();
  private sweepIntervalId: ReturnType<typeof setInterval>;

  private readonly DEFAULT_TTL_MS = 10 * 60 * 1000;  // 10 minutes
  private readonly MAX_ENTRIES    = 200;
  private readonly SWEEP_INTERVAL = 5  * 60 * 1000;  // 5 minutes

  constructor() {
    this.sweepIntervalId = setInterval(() => this.sweepExpired(), this.SWEEP_INTERVAL);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  set<T>(key: string, value: T, ttlMs: number = this.DEFAULT_TTL_MS): void {
    if (this.store.size >= this.MAX_ENTRIES) {
      this.evictLRU();
    }
    this.store.set(key, {
      value,
      expiresAt: Date.now() + ttlMs,
      hits: 0,
      createdAt: Date.now(),
    });
  }

  get<T>(key: string): T | null {
    const entry = this.store.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return null;
    }
    entry.hits++;
    return entry.value;
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  delete(key: string): void {
    this.store.delete(key);
  }

  /** Remove all entries — call during soft reload */
  flush(): void {
    this.store.clear();
  }

  get size(): number {
    return this.store.size;
  }

  // ─── Internal ────────────────────────────────────────────────────────────────

  private sweepExpired(): void {
    const now = Date.now();
    let swept = 0;
    for (const [key, entry] of this.store) {
      if (now > entry.expiresAt) {
        this.store.delete(key);
        swept++;
      }
    }
    if (swept > 0) {
      console.log(`[TtlCache] Swept ${swept} expired entries (${this.store.size} remaining)`);
    }
  }

  private evictLRU(): void {
    let lruKey: string | null = null;
    let minHits = Infinity;
    for (const [key, entry] of this.store) {
      if (entry.hits < minHits) {
        minHits = entry.hits;
        lruKey  = key;
      }
    }
    if (lruKey) {
      this.store.delete(lruKey);
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.sweepIntervalId);
    this.store.clear();
  }
}
